/**
 * GreetingsManager — begroetingen per merk: tekst schrijven (zelf of via de
 * merk-generator), TTS genereren met de gekozen stem, beluisteren, activeren.
 *
 * Multi-brand: de `brand`-prop scopet lijst, aanmaken en generator; per
 * (merk, kind) is er één actieve begroeting.
 *
 * Generator: preset-chips (gesloten / feestdag / druk moment / opname-melding)
 * of vrije opdracht → /api/telephony/greeting-suggest levert 2-3 varianten in
 * merkstijl; klik zet de tekst (bewerkbaar) in het tekstveld. Audio maken
 * blijft de bestaande flow: tekst → genereer → beluister → activeer.
 *
 * Praat met /api/telephony/greetings; audio komt uit de publieke
 * Storage-bucket (publicUrl), dus <audio> werkt direct.
 */

import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { supabase } from '../../../lib/supabase';
import type { VoicesData } from './VoicePicker';

interface Greeting {
  id: string;
  name: string;
  kind: 'voicemail' | 'closed' | 'announce' | 'hold';
  source: 'tts' | 'upload';
  tts_text: string | null;
  voice: string | null;
  model: string | null;
  active: boolean;
  created_at: string;
  publicUrl: string;
}

export interface GeneratorPrefill {
  preset: string;
  context: string;
  kind: Greeting['kind'];
  /** Uniek per verzoek zodat dezelfde prefill twee keer toegepast kan worden. */
  requestId: number;
}

const KIND_LABEL: Record<Greeting['kind'], string> = {
  closed: 'Buiten openingstijden',
  voicemail: 'Voicemail (binnen uren)',
  announce: 'Aankondiging (vóór rinkelen)',
  hold: 'Wachtmuziek (wachtstand)',
};

const PRESETS: Array<{ key: string; label: string; kind: Greeting['kind'] }> = [
  { key: 'gesloten', label: 'Gesloten', kind: 'closed' },
  { key: 'feestdag', label: 'Feestdag of vakantie', kind: 'closed' },
  { key: 'druk-moment', label: 'Druk moment', kind: 'voicemail' },
  { key: 'opname-melding', label: 'Opname-melding', kind: 'announce' },
  { key: 'wachtstand', label: 'Wachtstand-melding', kind: 'hold' },
  { key: 'vrij', label: 'Vrije opdracht', kind: 'closed' },
];

const MODEL_LABEL: Record<string, string> = {
  eleven_multilingual_v2: 'v2 (bewezen)',
  eleven_v3: 'v3 (expressiever)',
};

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  return headers;
}

interface Props {
  /** Merk waarvoor begroetingen beheerd worden (KLEDA/CINCO/...). */
  brand: string;
  voices?: VoicesData | null;
  prefill?: GeneratorPrefill | null;
  onCreated?: (greeting: { id: string; name: string; kind: string }) => void;
}

export function GreetingsManager({ brand, voices, prefill, onCreated }: Props) {
  const [greetings, setGreetings] = useState<Greeting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState('');
  const [kind, setKind] = useState<Greeting['kind']>('closed');
  const [ttsText, setTtsText] = useState('');
  const [voiceId, setVoiceId] = useState('');
  const [model, setModel] = useState('');

  const [preset, setPreset] = useState<string | null>(null);
  const [presetContext, setPresetContext] = useState('');
  const [variants, setVariants] = useState<string[]>([]);
  const [suggesting, setSuggesting] = useState(false);

  const fetchGreetings = useCallback(async () => {
    try {
      const r = await fetch(`/api/telephony/greetings?brand=${encodeURIComponent(brand)}`, {
        headers: await authHeaders(),
      });
      if (!r.ok) throw new Error(`greetings failed: ${r.status}`);
      const json = await r.json();
      setGreetings(json.greetings ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  }, [brand]);

  useEffect(() => {
    void fetchGreetings();
  }, [fetchGreetings]);

  // Prefill vanuit een uitzonderingsdag ("Schrijf begroeting voor deze dag").
  useEffect(() => {
    if (!prefill) return;
    setPreset(prefill.preset);
    setPresetContext(prefill.context);
    setKind(prefill.kind);
    setVariants([]);
  }, [prefill?.requestId]); // eslint-disable-line react-hooks/exhaustive-deps

  const suggest = useCallback(async () => {
    if (!preset) return;
    setSuggesting(true);
    setError(null);
    setVariants([]);
    try {
      const r = await fetch('/api/telephony/greeting-suggest', {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ situation: preset, context: presetContext.trim() || undefined, brand }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ error: 'Schrijven mislukt' }));
        throw new Error(err.error || 'Schrijven mislukt');
      }
      const json = (await r.json()) as { variants: string[]; kind: Greeting['kind'] | null };
      setVariants(json.variants ?? []);
      if (json.kind) setKind(json.kind);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSuggesting(false);
    }
  }, [preset, presetContext]);

  const generate = useCallback(async () => {
    if (!name.trim() || !ttsText.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fetch('/api/telephony/greetings', {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({
          name: name.trim(),
          kind,
          brand,
          tts_text: ttsText.trim(),
          ...(voiceId ? { voice: voiceId } : {}),
          ...(model ? { model } : {}),
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ error: 'Genereren mislukt' }));
        throw new Error(err.error || 'Genereren mislukt');
      }
      const json = (await r.json()) as { greeting: Greeting };
      setName('');
      setTtsText('');
      setVariants([]);
      await fetchGreetings();
      if (json.greeting) onCreated?.({ id: json.greeting.id, name: json.greeting.name, kind: json.greeting.kind });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [name, kind, brand, ttsText, voiceId, model, fetchGreetings, onCreated]);

  const toggleActive = useCallback(
    async (g: Greeting) => {
      setBusy(true);
      setError(null);
      try {
        const r = await fetch('/api/telephony/greetings', {
          method: 'PATCH',
          headers: await authHeaders(),
          body: JSON.stringify({ id: g.id, active: !g.active }),
        });
        if (!r.ok) throw new Error('Activeren mislukt');
        await fetchGreetings();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusy(false);
      }
    },
    [fetchGreetings],
  );

  const remove = useCallback(
    async (g: Greeting) => {
      if (!window.confirm(`Begroeting "${g.name}" verwijderen?`)) return;
      setBusy(true);
      setError(null);
      try {
        const r = await fetch(`/api/telephony/greetings?id=${encodeURIComponent(g.id)}`, {
          method: 'DELETE',
          headers: await authHeaders(),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({ error: 'Verwijderen mislukt' }));
          throw new Error(err.error || 'Verwijderen mislukt');
        }
        await fetchGreetings();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusy(false);
      }
    },
    [fetchGreetings],
  );

  return (
    <div className="space-y-5">
      {error && (
        <div className="px-3 py-2 bg-red-50 border border-red-200 text-red-800 rounded text-sm">{error}</div>
      )}

      {/* Kleda-generator */}
      <div className="space-y-2 pb-4 border-b border-slate-100">
        <div className="text-sm font-medium text-slate-800">Schrijf voor me (merkstijl)</div>
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => {
                setPreset(p.key);
                setKind(p.kind);
                setVariants([]);
              }}
              className={clsx(
                'px-2 py-1 rounded text-xs font-medium',
                preset === p.key ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
        {preset && (
          <div className="flex flex-wrap gap-2">
            <input
              type="text"
              placeholder={
                preset === 'feestdag'
                  ? 'Context (bijv. Koningsdag, dinsdag weer open)'
                  : preset === 'vrij'
                    ? 'Beschrijf de situatie'
                    : 'Extra context (optioneel)'
              }
              value={presetContext}
              onChange={(e) => setPresetContext(e.target.value)}
              className="border border-slate-200 rounded px-2 py-1.5 text-sm flex-1 min-w-[14rem]"
            />
            <button
              type="button"
              onClick={() => void suggest()}
              disabled={suggesting || (preset === 'vrij' && !presetContext.trim())}
              className={clsx(
                'px-3 py-1.5 rounded text-sm font-medium',
                suggesting || (preset === 'vrij' && !presetContext.trim())
                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700',
              )}
            >
              {suggesting ? 'Schrijven…' : 'Schrijf voor me'}
            </button>
          </div>
        )}
        {variants.length > 0 && (
          <div className="space-y-1.5">
            <div className="text-xs text-slate-500">Kies een variant (daarna nog bewerkbaar):</div>
            {variants.map((v, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setTtsText(v)}
                className={clsx(
                  'block w-full text-left px-3 py-2 rounded border text-sm',
                  ttsText === v
                    ? 'border-indigo-300 bg-indigo-50 text-slate-800'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-indigo-200 hover:bg-slate-50',
                )}
              >
                {v}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Nieuwe begroeting */}
      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            placeholder="Naam (bijv. Gesloten - standaard)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="border border-slate-200 rounded px-2 py-1.5 text-sm flex-1 min-w-[12rem]"
          />
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as Greeting['kind'])}
            className="border border-slate-200 rounded px-2 py-1.5 text-sm"
          >
            {(Object.entries(KIND_LABEL) as Array<[Greeting['kind'], string]>).map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </select>
        </div>
        <textarea
          placeholder="Tekst die de beller hoort. Wordt met AI-stem (Nederlands) omgezet naar audio."
          value={ttsText}
          onChange={(e) => setTtsText(e.target.value)}
          rows={3}
          className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm"
        />
        {voices && voices.voices.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
            <span className="text-slate-500">Stem</span>
            <select
              value={voiceId}
              onChange={(e) => setVoiceId(e.target.value)}
              className="border border-slate-200 rounded px-2 py-1 text-xs"
            >
              <option value="">Standaardstem</option>
              {voices.voices.map((v) => (
                <option key={v.voiceId} value={v.voiceId}>{v.name}</option>
              ))}
            </select>
            <span className="text-slate-500">Model</span>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="border border-slate-200 rounded px-2 py-1 text-xs"
            >
              <option value="">Standaard ({MODEL_LABEL[voices.defaultModel] ?? voices.defaultModel})</option>
              {voices.models.map((m) => (
                <option key={m} value={m}>{MODEL_LABEL[m] ?? m}</option>
              ))}
            </select>
          </div>
        )}
        <button
          type="button"
          onClick={generate}
          disabled={busy || !name.trim() || !ttsText.trim()}
          className={clsx(
            'px-3 py-1.5 rounded text-sm font-medium',
            busy || !name.trim() || !ttsText.trim()
              ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
              : 'bg-indigo-600 text-white hover:bg-indigo-700',
          )}
        >
          {busy ? 'Bezig…' : 'Genereer begroeting'}
        </button>
        <p className="text-xs text-slate-400">
          Nieuwe begroetingen staan uit tot je ze activeert. Per merk en soort is er één actieve
          begroeting; een uitzonderingsdag kan daarnaast een eigen begroeting hebben.
        </p>
      </div>

      {/* Bestaande begroetingen */}
      {isLoading ? (
        <div className="h-16 bg-slate-50 rounded animate-pulse" />
      ) : greetings.length === 0 ? (
        <p className="text-sm text-slate-500">
          Nog geen begroetingen. Zonder actieve begroeting hoort de beller een neutrale computerstem.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {greetings.map((g) => (
            <li key={g.id} className="py-3 flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-800">{g.name}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                    {KIND_LABEL[g.kind]}
                  </span>
                  {g.active && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium">
                      Actief
                    </span>
                  )}
                </div>
                {g.tts_text && (
                  <p className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap select-text">{g.tts_text}</p>
                )}
              </div>
              <audio controls preload="none" src={g.publicUrl} className="h-8 max-w-[14rem]" />
              <button
                type="button"
                onClick={() => void toggleActive(g)}
                disabled={busy}
                className={clsx(
                  'px-2.5 py-1 rounded text-xs font-medium',
                  g.active
                    ? 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    : 'bg-emerald-600 text-white hover:bg-emerald-700',
                )}
              >
                {g.active ? 'Deactiveren' : 'Activeren'}
              </button>
              {!g.active && (
                <button
                  type="button"
                  onClick={() => void remove(g)}
                  disabled={busy}
                  className="text-xs text-slate-400 hover:text-red-600"
                >
                  Verwijderen
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
